import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  ListItemText,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type { LocationFeatureDetails } from '../../../types/Location';
import type { FeatureDefinition, FeatureSubField } from '../featureRegistry';
import type { DetailValue } from '../featureSelection';

interface FeatureSubPanelProps {
  definition: FeatureDefinition;
  details: LocationFeatureDetails | null | undefined;
  onChange: (key: string, value: DetailValue) => void;
}

const fieldId = (feature: string, key: string) =>
  `feature-${feature.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${key}`;

/** Sub-fields shown under a checked feature. */
const FeatureSubPanel = ({ definition, details, onChange }: FeatureSubPanelProps) => {
  const renderField = (field: FeatureSubField) => {
    const id = fieldId(definition.feature, field.key);
    const value = details?.[field.key];

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.key}
            id={id}
            label={field.label}
            size="small"
            fullWidth
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            inputProps={{ maxLength: field.maxLength }}
          />
        );
      case 'number':
        return (
          <TextField
            key={field.key}
            id={id}
            label={field.label}
            size="small"
            fullWidth
            value={typeof value === 'number' ? String(value) : ''}
            onChange={(e) => {
              // Text input, not type="number": browsers keep invalid text like "10+" visible while
              // reporting value "", which desyncs React state and overlaps the label.
              const digits = e.target.value.replace(/\D/g, '');
              onChange(field.key, digits === '' ? null : Math.min(Math.max(Number(digits), field.min), field.max));
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: String(field.max).length }}
          />
        );
      case 'select': {
        const selectedOption = field.options.find((option) => option.value === value);
        return (
          <TextField
            key={field.key}
            id={id}
            label={field.label}
            size="small"
            select
            fullWidth
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            helperText={selectedOption?.description}
            SelectProps={{ renderValue: (selected) => field.options.find((o) => o.value === selected)?.label ?? '' }}
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {field.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                  secondaryTypographyProps={{ sx: { whiteSpace: 'normal', maxWidth: 280 } }}
                />
              </MenuItem>
            ))}
          </TextField>
        );
      }
      case 'toggle':
        return (
          <Box key={field.key}>
            <FormLabel id={`${id}-label`} sx={{ display: 'block', fontSize: 12, mb: 0.5 }}>
              {field.label}
            </FormLabel>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              aria-labelledby={`${id}-label`}
              value={typeof value === 'boolean' ? value : null}
              onChange={(_e, next: boolean | null) => onChange(field.key, next)}
            >
              <ToggleButton value={true} sx={{ px: 2 }}>Yes</ToggleButton>
              <ToggleButton value={false} sx={{ px: 2 }}>No</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        );
      case 'checkboxGroup': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <Box key={field.key} sx={{ gridColumn: '1 / -1' }}>
            <FormLabel id={`${id}-label`} sx={{ display: 'block', fontSize: 12, mb: 0.5 }}>
              {field.label}
            </FormLabel>
            <FormGroup
              role="group"
              aria-labelledby={`${id}-label`}
              sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', columnGap: 1 }}
            >
              {field.options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(option.value)}
                      onChange={(e) =>
                        onChange(
                          field.key,
                          e.target.checked
                            ? [...selected, option.value]
                            : selected.filter((v) => v !== option.value)
                        )
                      }
                    />
                  }
                  label={option.label}
                  slotProps={{ typography: { variant: 'body2' } }}
                />
              ))}
            </FormGroup>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box
      data-testid={`feature-subpanel-${definition.feature}`}
      role="group"
      aria-label={`${definition.label ?? definition.feature} details`}
      sx={{
        // Line sits under the checkbox; fields line up with the feature label text.
        ml: '9px',
        pl: '20px',
        mt: 0.5,
        mb: 1.5,
        py: 1,
        borderLeft: 2,
        borderColor: 'divider',
        display: 'grid',
        gap: 2,
        gridTemplateColumns: '1fr',
      }}
    >
      {definition.subFields.map(renderField)}
    </Box>
  );
};

export default FeatureSubPanel;
